import { useTranslation } from "react-i18next";
import ScrollReveal from "./ScrollReveal";
import vsBg from "@/assets/vs-bg.webp";
import problemBg from "@/assets/le-probleme-bg.webp";
import iconLeak from "@/assets/icons/fuite-invisible.webp";
import iconLegio from "@/assets/icons/risque-legionellose.webp";
import iconCompliance from "@/assets/icons/non-conformite-reglementaire.webp";

const ProblemSection = () => {
  const { t } = useTranslation();

  const painCards = [
    { icon: iconLeak, alt: "Pictogramme fuite d'eau invisible détectée par capteur connecté", title: t("problem.pain1Title"), body: t("problem.pain1Body") },
    { icon: iconLegio, alt: "Pictogramme risque de légionellose dans réseau d'eau de bâtiment", title: t("problem.pain2Title"), body: t("problem.pain2Body") },
    { icon: iconCompliance, alt: "Pictogramme non-conformité réglementaire eau sanitaire", title: t("problem.pain3Title"), body: t("problem.pain3Body") },
  ];

  const classic = t("problem.classicItems", { returnObjects: true }) as string[];
  const eaulistik = t("problem.eaulistikItems", { returnObjects: true }) as string[];

  return (
    <section id="problem" className="py-8 lg:py-14 relative" aria-labelledby="problem-heading">
      <div className="container mx-auto px-4 space-y-6 lg:space-y-10">
        <ScrollReveal>
          <div className="space-y-4 max-w-2xl">
            <p className="section-label">{t("problem.label")}</p>
            <h2 id="problem-heading" className="font-display text-3xl lg:text-5xl text-foreground">
              {t("problem.title")}
            </h2>
            <p className="text-muted-foreground font-body text-lg">{t("problem.desc")}</p>
          </div>
        </ScrollReveal>

        <ScrollReveal>
          <div className="group relative rounded-2xl overflow-hidden border border-primary/20 transition-all duration-500 hover:scale-[1.01] hover:shadow-lg hover:shadow-primary/10 hover:border-primary/40">
            <img src={problemBg} alt="Réseau d'eau vétuste en bâtiment — fuites et dégâts des eaux" className="w-full h-auto object-cover opacity-60 transition-opacity duration-500 group-hover:opacity-75" loading="lazy" width="1920" height="1080" />
            <div className="absolute inset-0 bg-background/30 transition-colors duration-500 group-hover:bg-background/15" />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background/95 via-background/70 to-transparent pt-32 pb-6 px-4 lg:px-6 space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                {painCards.map((c, i) => (
                  <div key={i} className="group backdrop-blur-md bg-card/50 border border-border/50 rounded-xl p-4 border-l-4 border-l-destructive/60 transition-all duration-500 hover:bg-card/80 hover:scale-[1.03] hover:shadow-lg hover:shadow-primary/10 hover:border-l-primary/80">
                    <div className="mb-2 transition-transform duration-500 group-hover:scale-110 group-hover:-translate-y-1">
                      <img src={c.icon} alt={c.alt} width="64" height="64" loading="lazy" decoding="async" className="w-14 h-14 object-contain" />
                    </div>
                    <h3 className="font-display text-lg text-foreground mb-1 transition-colors duration-300 group-hover:text-primary">{c.title}</h3>
                    <p className="text-xs text-muted-foreground font-body leading-relaxed transition-colors duration-300 group-hover:text-foreground/80">{c.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ScrollReveal>

        <div className="grid md:grid-cols-2 gap-6">
          <ScrollReveal>
            <div className="card-surface p-6 space-y-3">
              <span className="text-xs font-body font-medium text-gold uppercase tracking-wider">{t("problem.classic")}</span>
              {classic.map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-muted-foreground font-body">
                  <span className="text-destructive mt-0.5">✕</span>
                  {item}
                </div>
              ))}
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.15}>
            <div className="card-surface p-6 space-y-3 border-primary/40">
              <span className="text-xs font-body font-medium text-primary uppercase tracking-wider">Eaulistik</span>
              {eaulistik.map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-foreground font-body">
                  <span className="text-accent mt-0.5">✓</span>
                  {item}
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>

        <ScrollReveal>
          <div className="group relative rounded-2xl overflow-hidden border border-primary/20 transition-all duration-500 hover:scale-[1.01] hover:shadow-lg hover:shadow-primary/10 hover:border-primary/40">
            <img src={vsBg} alt="Comparaison gestion classique vs Eaulistik — avant/après monitoring intelligent" className="w-full h-auto object-cover opacity-60 transition-opacity duration-500 group-hover:opacity-75" loading="lazy" width="1920" height="905" />
            <div className="absolute inset-0 bg-background/30 transition-colors duration-500 group-hover:bg-background/15" />
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default ProblemSection;
